import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import type { PlayerWithMarkets, Sport, LineupInfo } from '@sharpr/types';
import { useAccess } from './useAccess';
import { oddsPlayersQuery } from './useOddsBoard';
import { resolveNextGameEntry } from '../utils/nextGameEntry';

const BATTER_POSITION_ORDER: Record<string, number> = {
  C: 1, '1B': 2, '2B': 3, '3B': 4, SS: 5,
  LF: 6, CF: 6, RF: 6, OF: 6, DH: 7,
};
const PITCHER_POSITIONS = new Set(['P', 'SP', 'RP']);

function isPitcher(p: PlayerWithMarkets): boolean {
  return PITCHER_POSITIONS.has(p.position ?? '');
}

export interface SlatePlayers {
  battersByTeam: Map<string, PlayerWithMarkets[]>;
  /** Own-team lineup keyed by `${team}|${gameDate}` (YYYY-MM-DD, ET) when the lineup
   *  carries a date, else by team alone. Lets a team playing on consecutive days
   *  render the right lineup on each game's card. Use `lineupKey(team, date)` to read. */
  lineupByTeam: Map<string, LineupInfo>;
  /** All players (batters + pitchers) keyed by id, so the slate can re-use the props-API
   *  row when opening research — same factory both surfaces. */
  playersById: Map<string, PlayerWithMarkets>;
  isLoading: boolean;
}

/** Composes the `lineupByTeam` map key. Strict — a team playing today and tomorrow
 *  needs distinct keys, and a lineup whose `gameDate` doesn't match the rendered
 *  game's date must NOT fall back to the team-only bucket (that's the bug we're fixing). */
export function lineupKey(team: string, gameDate: string): string {
  return `${team}|${gameDate}`;
}

/**
 * Shares the ['players', 'odds', sport, tier] cache with PlayerSelectionView so the slate
 * directory reuses the same bulk payload the props screen already fetched. The slate page
 * is PUBLIC, so routing by tier here is load-bearing: anon/no-sub viewers route to the
 * public edge-cached endpoint instead of 401ing on the authed one.
 */
export function useSlatePlayers(sport: Sport = 'mlb'): SlatePlayers {
  const access = useAccess();
  // The view, as the props screen asks: an admin previewing shares its cache entry.
  const view = access.view(sport);

  const { data, isLoading } = useQuery({
    ...oddsPlayersQuery(sport, view),
    staleTime: 60_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    // Gate on the (fast, local) session resolving so the first fetch uses the right
    // endpoint instead of fetching as anon then refetching once the claim is read.
    enabled: !access.loading,
    // Survive the claim->live access correction (key index 3 flips) without
    // blanking — keep prior rows across that flip, mirroring PlayerSelectionView. `sport`
    // is fixed here so index 2 never changes, but guard on it to match the sibling exactly.
    placeholderData: (prev, prevQuery) =>
      prevQuery && prevQuery.queryKey[2] === sport ? prev : undefined,
  });

  const { battersByTeam, lineupByTeam, playersById } = useMemo(() => {
    const batters = new Map<string, PlayerWithMarkets[]>();
    const lineups = new Map<string, LineupInfo>();
    const byId = new Map<string, PlayerWithMarkets>();
    for (const p of data?.players ?? []) {
      // Doubleheader: a player has one entry per game sharing an id. Resolve
      // the id to the NEXT uncommenced game (pregame of G1 → G1; after G1
      // commences → G2) instead of last-writer-wins in blob order.
      const existing = byId.get(p.id);
      byId.set(p.id, existing ? (resolveNextGameEntry([existing, p]) ?? p) : p);
      if (isPitcher(p)) continue;
      // Batter `lineup` references OWN team's lineup; pitcher `lineup` references the
      // opposing team's lineup. Capture only from batters so the map is keyed correctly.
      // Key includes gameDate so BOS playing today + tomorrow surfaces both lineups.
      if (p.lineup) {
        const k = lineupKey(p.team, p.lineup.gameDate);
        if (!lineups.has(k)) lineups.set(k, p.lineup);
      }
      const list = batters.get(p.team);
      if (list) list.push(p);
      else batters.set(p.team, [p]);
    }
    for (const list of batters.values()) {
      list.sort((a, b) => {
        const ap = BATTER_POSITION_ORDER[a.position ?? ''] ?? 50;
        const bp = BATTER_POSITION_ORDER[b.position ?? ''] ?? 50;
        if (ap !== bp) return ap - bp;
        return a.name.localeCompare(b.name);
      });
    }
    return { battersByTeam: batters, lineupByTeam: lineups, playersById: byId };
  }, [data?.players]);

  return { battersByTeam, lineupByTeam, playersById, isLoading };
}
